import { Platform } from 'react-native';

import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';
import { 
  PremiumFeature, 
  SubscriptionTier, 
  TIER_FEATURES,
  normalizeSubscriptionTier,
} from '../types/premiumFeatures';

import { revenueCatService } from './revenueCatService';

export interface FeatureEntitlement {
  id: string;
  userId: string;
  featureKey: PremiumFeature;
  entitlementSource: 'subscription' | 'purchase' | 'grant' | 'trial' | 'promotion';
  sourceId?: string;
  validFrom: Date;
  validUntil?: Date;
  isActive: boolean;
  metadata?: Record<string, unknown>;
}

export interface UserSubscription {
  id: string;
  userId: string;
  subscriptionType: SubscriptionTier;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'unpaid';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  paymentProvider: 'stripe' | 'apple' | 'google';
  paymentProviderId: string;
  billingInterval?: 'monthly' | 'annual';
  metadata?: Record<string, unknown>;
}

interface ActiveSubscriptionRpcRow {
  subscription_id?: string;
  id?: string;
  subscription_type?: string;
  status: UserSubscription['status'];
  current_period_start?: string;
  starts_at?: string;
  current_period_end?: string;
  expires_at?: string;
  cancel_at_period_end?: boolean;
  provider?: UserSubscription['paymentProvider'];
  payment_provider_id?: string;
  billing_interval?: 'monthly' | 'annual';
  metadata?: Record<string, unknown>;
}

interface FamilyMembershipRow {
  family_id: string;
}

interface FamilyOwnerRow {
  user_id: string;
}

export class EntitlementService {
  private async getNativeRevenueCatSubscription(userId: string): Promise<UserSubscription | null> {
    const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';
    if (!isNativeMobile) {
      return null;
    }

    const revenueCatSubscription = await revenueCatService.getSubscriptionInfo();
    if (!revenueCatSubscription?.isActive) {
      return null;
    }

    return {
      id: revenueCatSubscription.productIdentifier,
      userId,
      subscriptionType: SubscriptionTier.PREMIUM,
      status: 'active',
      currentPeriodStart: revenueCatSubscription.originalPurchaseDate,
      currentPeriodEnd: revenueCatSubscription.expirationDate || new Date(),
      cancelAtPeriodEnd: !revenueCatSubscription.willRenew,
      paymentProvider: Platform.OS === 'ios' ? 'apple' : 'google',
      paymentProviderId: revenueCatSubscription.productIdentifier,
      billingInterval: revenueCatSubscription.billingInterval,
      metadata: {
        premiumAccessSource: 'revenuecat_native',
        entitlements: revenueCatSubscription.entitlements,
      },
    };
  }

  private async getDirectUserSubscription(userId: string): Promise<UserSubscription | null> {
    const { data: subscription, error } = await supabase
      .rpc('get_user_active_subscription', {
        p_user_id: userId
      });

    if (error) {
      console.error('❌ [ENTITLEMENT] RPC error:', error);
      throw error;
    }

    if (!subscription || subscription.length === 0) {
      return null;
    }

    const sub = subscription[0] as ActiveSubscriptionRpcRow;
    const normalizedTier = normalizeSubscriptionTier(sub.subscription_type);

    if (normalizedTier === SubscriptionTier.FREE) {
      return null;
    }

    return {
      id: sub.subscription_id || sub.id || '',
      userId,
      subscriptionType: normalizedTier,
      status: sub.status,
      currentPeriodStart: new Date(sub.current_period_start || sub.starts_at || new Date().toISOString()),
      currentPeriodEnd: new Date(sub.current_period_end || sub.expires_at || new Date().toISOString()),
      cancelAtPeriodEnd: sub.cancel_at_period_end || false,
      paymentProvider: sub.provider || 'stripe',
      paymentProviderId: sub.payment_provider_id || '',
      billingInterval: sub.billing_interval,
      metadata: sub.metadata,
    };
  }

  private async getInheritedFamilyPremiumSubscription(userId: string): Promise<UserSubscription | null> {
    const { data: memberships, error: membershipError } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', userId)
      .eq('status', 'approved');

    if (membershipError) {
      throw membershipError;
    }

    const familyIds = (memberships as FamilyMembershipRow[] | null)?.map((membership) => membership.family_id) || [];
    if (familyIds.length === 0) {
      return null;
    }

    for (const familyId of familyIds) {
      const { data: owners, error: ownerError } = await supabase
        .from('family_members')
        .select('user_id')
        .eq('family_id', familyId)
        .eq('role', 'owner')
        .eq('status', 'approved')
        .limit(1);

      if (ownerError) {
        throw ownerError;
      }

      const ownerUserId = (owners as FamilyOwnerRow[] | null)?.[0]?.user_id;
      if (!ownerUserId || ownerUserId === userId) {
        continue;
      }

      const ownerSubscription = await this.getDirectUserSubscription(ownerUserId);
      if (ownerSubscription && ownerSubscription.status === 'active') {
        return {
          ...ownerSubscription,
          userId,
          subscriptionType: SubscriptionTier.PREMIUM,
          metadata: {
            ...(ownerSubscription.metadata || {}),
            premiumAccessSource: 'family_inherited',
            subscriptionOwnerUserId: ownerUserId,
            familyId,
          },
        };
      }
    }

    return null;
  }

  /**
   * Check if a user has access to a premium feature
   */
  async hasFeatureAccess(userId: string, feature: PremiumFeature): Promise<boolean> {
    try {
      // If monetization is disabled globally, grant access to all features
      console.log('[EntitlementService] MONETIZATION_ENABLED:', ENV_CONFIG.FEATURES.MONETIZATION_ENABLED);
      if (!ENV_CONFIG.FEATURES.MONETIZATION_ENABLED) {
        console.log('🚨 [EntitlementService] MONETIZATION DISABLED - Allowing all features');
        return true;
      }

      const subscription = await this.getUserSubscription(userId);
      if (
        subscription &&
        subscription.status === 'active' &&
        normalizeSubscriptionTier(subscription.subscriptionType) === SubscriptionTier.PREMIUM &&
        TIER_FEATURES[SubscriptionTier.PREMIUM].includes(feature)
      ) {
        return true;
      }

      // Check for valid entitlement
      console.log('[EntitlementService] Checking feature access for:', { userId, feature });
      const { data: entitlement, error } = await supabase
        .rpc('has_feature_access', {
          p_feature_key: feature,
          p_user_id: userId
        });

      console.log('[EntitlementService] RPC result:', { entitlement, error });

      if (error) {
        console.error('🚨 [EntitlementService] RPC ERROR - This should block access:', error);
        // SECURITY FIX: Default to DENY access on error instead of allowing
        console.log('🚨 [EntitlementService] Defaulting to FALSE due to error for security');
        return false;
      }

      // Extract has_access from the returned array
      if (Array.isArray(entitlement) && entitlement.length > 0) {
        const result = entitlement[0];
        console.log('[EntitlementService] Extracted result:', result);
        console.log(`🔒 [EntitlementService] Access Decision: ${result.has_access ? 'GRANTED' : 'DENIED'} for feature ${feature}`);
        return result.has_access || false;
      }

      console.log('🚨 [EntitlementService] No valid result from RPC - DENYING access');
      return false;
    } catch (error) {
      console.error('🚨 [EntitlementService] CATCH ERROR - This should block access:', error);
      // SECURITY FIX: Default to DENY access on error for security
      console.log('🚨 [EntitlementService] Defaulting to FALSE due to catch error for security');
      return false;
    }
  }

  /**
   * Grant feature access based on subscription
   */
  async grantSubscriptionFeatures(userId: string, subscriptionType: SubscriptionTier): Promise<void> {
    try {
      const normalizedTier = normalizeSubscriptionTier(subscriptionType);
      const features = TIER_FEATURES[normalizedTier];
      
      // Get subscription record to use as source_id
      const { data: subscription, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select('id')
        .eq('user_id', userId)
        .in('subscription_type', [subscriptionType, normalizedTier])
        .eq('status', 'active')
        .single();

      if (subscriptionError || !subscription) {
        throw new Error('No active subscription found');
      }

      // Grant each feature
      const entitlements = features.map(feature => ({
        user_id: userId,
        feature_key: feature,
        entitlement_source: 'subscription' as const,
        source_id: subscription.id,
        valid_from: new Date().toISOString(),
        valid_until: null, // Permanent while subscription is active
        is_active: true,
        metadata: {
          subscription_type: normalizedTier,
          granted_at: new Date().toISOString()
        }
      }));

      const { error } = await supabase
        .from('feature_entitlements')
        .upsert(entitlements, {
          onConflict: 'user_id,feature_key,entitlement_source,source_id'
        });

      if (error) {
        throw error;
      }

      console.log(`Granted ${features.length} features to user ${userId} for ${subscriptionType} subscription`);
    } catch (error) {
      console.error('Failed to grant subscription features:', error);
      throw error;
    }
  }

  /**
   * Grant feature access for a purchased item
   */
  async grantPurchasedFeature(
    userId: string, 
    feature: PremiumFeature, 
    purchaseId: string, 
    expiryDate?: Date
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_entitlements')
        .upsert({
          user_id: userId,
          feature_key: feature,
          entitlement_source: 'purchase',
          source_id: purchaseId,
          valid_from: new Date().toISOString(),
          valid_until: expiryDate ? expiryDate.toISOString() : null,
          is_active: true,
          metadata: {
            purchase_id: purchaseId,
            granted_at: new Date().toISOString()
          }
        }, {
          onConflict: 'user_id,feature_key,entitlement_source,source_id'
        });

      if (error) {
        throw error;
      }

      console.log(`Granted feature ${feature} to user ${userId} for purchase ${purchaseId}`);
    } catch (error) {
      console.error('Failed to grant purchased feature:', error);
      throw error;
    }
  }

  /**
   * Grant temporary feature access (trial, promotion, etc.)
   */
  async grantTemporaryFeature(
    userId: string,
    feature: PremiumFeature,
    source: 'trial' | 'promotion' | 'grant',
    expiryDate: Date,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_entitlements')
        .insert({
          user_id: userId,
          feature_key: feature,
          entitlement_source: source,
          valid_from: new Date().toISOString(),
          valid_until: expiryDate.toISOString(),
          is_active: true,
          metadata: {
            ...metadata,
            granted_at: new Date().toISOString(),
            expires_at: expiryDate.toISOString()
          }
        });

      if (error) {
        throw error;
      }

      console.log(`Granted temporary feature ${feature} to user ${userId} until ${expiryDate}`);
    } catch (error) {
      console.error('Failed to grant temporary feature:', error);
      throw error;
    }
  }

  /**
   * Revoke feature access
   */
  async revokeFeature(userId: string, feature: PremiumFeature, source?: string): Promise<void> {
    try {
      let query = supabase
        .from('feature_entitlements')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('feature_key', feature);

      if (source) {
        query = query.eq('entitlement_source', source);
      }

      const { error } = await query;

      if (error) {
        throw error;
      }

      console.log(`Revoked feature ${feature} for user ${userId}`);
    } catch (error) {
      console.error('Failed to revoke feature:', error);
      throw error;
    }
  }

  /**
   * Get all features a user has access to
   */
  async getUserFeatures(userId: string): Promise<PremiumFeature[]> {
    try {
      // If monetization is disabled globally, return all features
      if (!ENV_CONFIG.FEATURES.MONETIZATION_ENABLED) {
        return Object.values(PremiumFeature);
      }

      // Get active entitlements
      const { data: entitlements, error } = await supabase
        .from('feature_entitlements')
        .select('feature_key')
        .eq('user_id', userId)
        .eq('is_active', true)
        .or('valid_until.is.null,valid_until.gt.' + new Date().toISOString());

      if (error) {
        throw error;
      }

      const directFeatures = (entitlements || []).map(e => e.feature_key as PremiumFeature);
      const subscription = await this.getUserSubscription(userId);

      if (subscription && subscription.status === 'active') {
        return Array.from(new Set([
          ...directFeatures,
          ...TIER_FEATURES[normalizeSubscriptionTier(subscription.subscriptionType)],
        ]));
      }

      return directFeatures;
    } catch (error) {
      console.error('Failed to get user features:', error);
      return [];
    }
  }

  /**
   * Get user's active subscription
   */
  async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    console.log('🔍 [ENTITLEMENT] Getting subscription for user:', userId);

    // Layer 1: Direct Supabase subscription (most authoritative)
    try {
      const directSubscription = await this.getDirectUserSubscription(userId);
      if (directSubscription) {
        return directSubscription;
      }
    } catch (error) {
      console.warn('⚠️ [ENTITLEMENT] Direct subscription check failed (continuing):', error);
    }

    // Layer 2: RevenueCat native subscription (iOS/Android store purchases)
    try {
      const revenueCatSubscription = await this.getNativeRevenueCatSubscription(userId);
      if (revenueCatSubscription) {
        return revenueCatSubscription;
      }
    } catch (error) {
      console.warn('⚠️ [ENTITLEMENT] RevenueCat check failed (continuing):', error);
    }

    // Layer 3: Inherited family premium
    try {
      return await this.getInheritedFamilyPremiumSubscription(userId);
    } catch (error) {
      console.warn('⚠️ [ENTITLEMENT] Family inheritance check failed:', error);
      return null;
    }
  }

  /**
   * Check whether the user personally owns an active Premium subscription.
   * This intentionally excludes inherited family access so host-only actions
   * like creating multiplayer rooms stay tied to the paying account.
   */
  async hasDirectPremiumSubscription(userId: string): Promise<boolean> {
    try {
      const subscription = await this.getDirectUserSubscription(userId);
      return (
        subscription !== null &&
        subscription.status === 'active' &&
        normalizeSubscriptionTier(subscription.subscriptionType) === SubscriptionTier.PREMIUM
      );
    } catch (error) {
      console.error('Failed to check direct premium subscription:', error);
      return false;
    }
  }

  /**
   * Check whether the current signed-in user can act as the Premium room sponsor.
   * On mobile, RevenueCat is the freshest source of direct ownership after purchase/restore.
   * We still fall back to the direct Supabase subscription record for older synced accounts.
   */
  async hasPremiumHostAccess(userId: string): Promise<boolean> {
    try {
      const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

      if (isNativeMobile) {
        const revenueCatSubscription = await revenueCatService.getSubscriptionInfo();
        if (revenueCatSubscription?.isActive) {
          return true;
        }
      }

      return await this.hasDirectPremiumSubscription(userId);
    } catch (error) {
      console.error('Failed to check premium host access:', error);
      return false;
    }
  }

  /**
   * Check if user has an active subscription
   */
  async hasActiveSubscription(userId: string): Promise<boolean> {
    try {
      const subscription = await this.getUserSubscription(userId);
      return subscription !== null &&
             subscription.status === 'active' &&
             subscription.subscriptionType !== SubscriptionTier.FREE;
    } catch (error) {
      console.error('Failed to check active subscription:', error);
      return false;
    }
  }

  /**
   * Get current plan details for display
   */
  async getCurrentPlan(userId: string): Promise<{ name: string; tier: SubscriptionTier } | null> {
    try {
      const subscription = await this.getUserSubscription(userId);

      if (!subscription || subscription.status !== 'active' || subscription.subscriptionType === SubscriptionTier.FREE) {
        return null;
      }

      // Map subscription tier to display names
      const planNames: Record<SubscriptionTier, string> = {
        [SubscriptionTier.FREE]: 'Free',
        [SubscriptionTier.PREMIUM]: 'Premium',
        [SubscriptionTier.INDIVIDUAL]: 'Premium',
        [SubscriptionTier.FAMILY]: 'Premium',
        [SubscriptionTier.GROUP_SMALL]: 'Premium',
        [SubscriptionTier.GROUP_MEDIUM]: 'Premium',
        [SubscriptionTier.GROUP_LARGE]: 'Premium'
      };

      return {
        name: planNames[subscription.subscriptionType] || 'Premium',
        tier: subscription.subscriptionType
      };
    } catch (error) {
      console.error('Failed to get current plan:', error);
      return null;
    }
  }

  /**
   * Get subscription tier for user
   */
  async getUserSubscriptionTier(userId: string): Promise<SubscriptionTier> {
    try {
      const subscription = await this.getUserSubscription(userId);
      return normalizeSubscriptionTier(subscription?.subscriptionType);
    } catch (error) {
      console.error('Failed to get user subscription tier:', error);
      return SubscriptionTier.FREE;
    }
  }

  /**
   * Check if user can access a specific subscription tier feature
   */
  async canAccessTierFeature(userId: string, requiredTier: SubscriptionTier): Promise<boolean> {
    try {
      const userTier = await this.getUserSubscriptionTier(userId);
      
      // Define tier hierarchy
      const normalizedUserTier = normalizeSubscriptionTier(userTier);
      const normalizedRequiredTier = normalizeSubscriptionTier(requiredTier);
      const tierHierarchy: Record<SubscriptionTier.FREE | SubscriptionTier.PREMIUM, number> = {
        [SubscriptionTier.FREE]: 0,
        [SubscriptionTier.PREMIUM]: 1,
      };

      return tierHierarchy[normalizedUserTier] >= tierHierarchy[normalizedRequiredTier];
    } catch (error) {
      console.error('Failed to check tier feature access:', error);
      return false;
    }
  }

  /**
   * Sync entitlements with subscription status
   */
  async syncSubscriptionEntitlements(userId: string): Promise<void> {
    try {
      const subscription = await this.getUserSubscription(userId);
      
      if (!subscription || subscription.status !== 'active') {
        // Deactivate all subscription-based entitlements
        await supabase
          .from('feature_entitlements')
          .update({ 
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('entitlement_source', 'subscription');
      } else {
        // Ensure user has all features for their subscription tier
        await this.grantSubscriptionFeatures(userId, subscription.subscriptionType);
      }
    } catch (error) {
      console.error('Failed to sync subscription entitlements:', error);
      throw error;
    }
  }

  /**
   * Clean up expired entitlements
   */
  async cleanupExpiredEntitlements(): Promise<void> {
    try {
      const { error } = await supabase
        .from('feature_entitlements')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .lt('valid_until', new Date().toISOString())
        .eq('is_active', true);

      if (error) {
        throw error;
      }

      console.log('Cleaned up expired entitlements');
    } catch (error) {
      console.error('Failed to cleanup expired entitlements:', error);
      throw error;
    }
  }
}

// Singleton instance
export const entitlementService = new EntitlementService();

// Default export
export default entitlementService;
